import { RefObject, useEffect, useState } from 'react'

/**
 * Custom hook that indicates if an element is visible inside the viewport.
 *
 * @param element - React ref object
 * @returns boolean - true if element is visible inside the viewport, false otherwise
 *
 * @example
 *
 * const elementRef = useRef(null)
 * const isVisible = useIntersectionObserver(elementRef)
 *
 * <div ref={elementRef}>Hello World</div>
 *
 * {isVisible && <div>Visible</div>}
 *
 */
export const useIntersectionObserver = (element: RefObject<HTMLElement>): boolean => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const current = element?.current
    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting)
    })
    current && observer?.observe(current)

    return () => {
      if (current) {
        observer.unobserve(current)
      }
    }
  }, [element])

  return isVisible
}
